import React, { ReactElement, ReactNode, useEffect, useRef, useState } from 'react';
import { Button, Col, Row, Spin, Tooltip } from 'antd';
import { RoomDetail } from '../../../../../../shared/models/room.model';
import Panel from '../../../../../../shared/components/card/Panel';
import { APPLICATION_ID, authFeatureConstants, FILE_UPLOAD, GUTTOR_SPACE_SMALL, TOOLTIP_TEXT } from '../../../../../../shared/constants/commonConstants';
import EditRoom from '../../../../shared/EditRoom';
import { FETCH_ROOM_TYPES } from '../../../../../../shared/constants/urlEndPoints';
import SendRequest from '../../../../../../shared/services/sendRequest.service';
import _ from 'lodash';
import { canActivate, GetFirstElement } from '../../../../../../utils/utility';
import NoDataFound from '../../../../../inventry/shared/NoDataFound';
import './Rooms.less';
import { UploadFile } from '../../../../../../shared/models/uploadFiles.model';
import { useSelector } from 'react-redux';
import { ReactComponent as CloseIcon } from '../../../../../../assets/images/svg/i_close.svg';

const Rooms = ({ roomsDetail, getRooms }: any): ReactElement => {
  const [rooms, setRooms] = useState<RoomDetail[]>(roomsDetail);
  const ref = useRef({ editRoom: null as any });
  const [visible, setVisible] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [roomTypes, setRoomTypes] = useState(null as any);
  const [selectedRoomType, setSelectedRoomType] = useState(null as any);
  const isAccessible = canActivate(authFeatureConstants.EDIT_HOSPITAL_PROFILE);
  let photos: any[] = [];
  const amenityMasterList = useSelector(
    ({ hospital: { amenityMaster } }: any): any => amenityMaster,
  );
  useEffect((): void => {
    if (roomsDetail?.length !== rooms?.length || isEdited) {
      getRooms(rooms);
    }
  }, [rooms]);

  useEffect((): void => {
    getRoomsTypes();
  }, []);

  useEffect((): void => {
    setRooms(roomsDetail);
    setVisible(false);
  }, [roomsDetail]);

  const setSelectedPhotos = (p: any): void => {
    const index = photos?.findIndex((x: any): any => x.id === p.id);
    if (index !== -1 && index !== undefined) {
      photos[index].photos.push(p.photo);
      // setPhotos(photos);
      photos = photos;
    } else {
      if (photos) {
        photos = [...photos, { id: p.id, photos: [p.photo] }];
      } else {
        photos = [{ id: p.id, photos: [p.photo] }];
      }
    }
  };
  const getRoomsTypes = async (): Promise<void> => {
    const url = `${process.env.REACT_APP_BASE_URL}${FETCH_ROOM_TYPES}`;
    const response = await SendRequest.instance.get(url);
    if (response && response.isSuccess) {
      setRoomTypes(response.lookUpData);
    }
  };

  const onAddRooms = (room?: any, indx?: number): any => {
      const temp: any = _.cloneDeep(room) || {};
      const uploadedPhotos: any = temp ? temp?.files?.filter((vv: any): any => vv.isActive) : null;
      uploadedPhotos ? temp.files = [...uploadedPhotos] : temp.files = [];
      const file = getRoomPhotosCount(temp)?.filteredPhotos;
      temp.files = file && file.length ? _.uniqBy(file, 'fileId') : temp.files;
      temp.indx = (indx !== undefined) ? (indx + 1) : undefined;
      ref.current.editRoom = temp;
      setVisible(true);
  };
  const onRoomSave = async (room: RoomDetail, alias?: string): Promise<any> => {
    let response: UploadFile = null as any;
    if (photos && photos.length > 0) {
      if (alias) {
        photos = photos.filter((v: any): any => v.id !== alias);
      } else {
        const length = photos.length;
        const formData = new FormData();
        formData.append('FileUploadType', 'Report');
        formData.append('ApplicationId', APPLICATION_ID.toString());
        const files: any = photos[length - 1].photos?.flat();
        const url = `${process.env.REACT_APP_BASE_URL}${FILE_UPLOAD}`;
        setIsUploading(true);
        setVisible(false);
        response = await SendRequest.instance.upload(
          url,
          files.filter((x: any): any => !x.fileId).map((y: any): any => y.file),
          formData,
        ).finally((): any => {
          setIsUploading(false);
        });
      }
    }
    const upldFiles = response?.fileUploadDetails as any;
    setIsEdited(true);
    if (room.indx || room?.id || room?.typeId) {
      if (rooms && rooms?.length) {
        const tempRooms = rooms?.filter((v: RoomDetail): boolean => (v.indx === room.indx)).map((newRoom: RoomDetail): any => {
          return {
            ...newRoom,
            typeName: room?.typeName,
            roomAlias: room?.roomAlias,
            roomCount: room?.roomCount,
            isNew: true,
            isActive: room?.isActive,
            typeId: selectedRoomType ? selectedRoomType.id : newRoom?.typeId,
            bedsCountPerRoom: room?.bedsCountPerRoom,
            amenities: room?.amenities,
            files: getFiles(response, room),
          };
        });
        const tempRoom: any[] = [...rooms];
        tempRoom.splice((room && room?.indx ? room?.indx - 1 : 0), 1, GetFirstElement(tempRooms));

        setRooms([...tempRoom]);
      }
    } else {
      room.id = 0;
      if (room.isNew) {
        room.isActive = room?.isActive;
      } else {
        room.isActive = true;
      }
      room.indx = (rooms && rooms?.length) ? (rooms?.length + 1) : 0;
      room.isNew = true;
      room.files = upldFiles && upldFiles.length ?
        upldFiles.map((file: any): any => ({ ...file, fileId: file.fileGuid, isActive: true })) : [];

      room.amenities = room && room?.selectedAmenities && room?.selectedAmenities?.length && amenityMasterList ?
        amenityMasterList.filter((v: any): any => {
          return room?.selectedAmenities?.findIndex((vv: any): any => v.value === vv) !== -1;
        })?.map((amenity: any): any => ({ ...amenity, amenityId: amenity.id })) : [];
      room.typeId = selectedRoomType.id;
      setRooms(rooms && rooms?.length ? [...rooms, room] : [room]);
    }
    setVisible(false);
  };

  const getFiles = (response: UploadFile, newRoom: RoomDetail): any => {
    if (response && response?.isSuccess && response?.fileUploadDetails) {
      const tempFiles: any = response?.fileUploadDetails?.map((v: any): any => ({ ...v, fileId: v.fileGuid, isActive: true }));
      return newRoom?.files ? [...tempFiles, ...newRoom?.files.filter((x: any): any => x.fileId)] : [...tempFiles];
    } else {
      return newRoom?.files;
    }
  };

  const onRoomtypeChange = (e: any): any => {
    setSelectedRoomType(GetFirstElement(roomTypes?.filter((v: any): boolean => v.value === e)));
  };

  const onDeleteRoom = (room: RoomDetail): any => {
    room.isActive = false;
    onRoomSave(room, room?.roomAlias);
  };

  const getRoomPhotosCount = (room: RoomDetail): any => {
    let filteredPhotos: any[] = [];
    let count = 0;
    const defRooms: any = roomsDetail && roomsDetail?.length;
    const selectedRoom: any = defRooms ?
      roomsDetail.find((v: RoomDetail): any => (v.roomAlias === room?.roomAlias && v.typeId === room.typeId)) : null;
    if (room && room.isNew && room?.isActive && room?.files && room?.files?.length) {
      filteredPhotos = [...room?.files?.filter((x: any): any => x?.isActive)];
    } else if (defRooms) {
      filteredPhotos = selectedRoom ? [...selectedRoom?.files] : [];
    }
    count = filteredPhotos && filteredPhotos?.length ? _.uniqBy(filteredPhotos, 'fileId').length : 0;
    return { count, filteredPhotos };
  };

  return (
    <>
      <Row>
        <h5 className="h5 mt-lg">Rooms</h5>
      </Row>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
      }}>
        {!isUploading ?
          <Row gutter={GUTTOR_SPACE_SMALL}>
            {(rooms && rooms.length > 0) && rooms.map((room: RoomDetail, i: number): ReactNode =>
              <>
                {room.isActive &&
                  <Col key={i + 10} md={6}>
                    <Panel onClick={(): any => onAddRooms(room, i)}
                      className={'infra-rooms-card ' + ((room?.isNew && room?.isActive) ? ' active' : room?.isActive ? '' : ' in-active')}>
                      <Tooltip title={room?.roomAlias}>
                        <div className="fsize_lg bold text-elipsis">{room?.roomAlias}</div>
                      </Tooltip>
                      {
                        isAccessible &&
                        <Tooltip title={TOOLTIP_TEXT.remove}>
                          <CloseIcon
                            onClick={(e: any): void => { e.stopPropagation(); onDeleteRoom(room); }}
                            className={'room-close fill-close'} />
                        </Tooltip>
                      }
                      <Tooltip title={room.typeName}>
                        <div className="label-title text-elipsis">Room type: <b>{room.typeName}</b></div>
                      </Tooltip>
                      <div className="label-title">Rooms: <b>{room?.roomCount}</b></div>
                      <div className="label-title">Beds per room: <b>{room?.bedsCountPerRoom}</b></div>
                      <div className="label-title">Images: <b>{getRoomPhotosCount(room)?.count}</b></div>
                      <div className="label-title">Amenities: <b>{room?.amenities?.filter((x: any): any => x.isActive)?.length}</b></div>
                    </Panel>
                  </Col>
                }
              </>,
            )}
            {(rooms && rooms?.length === 1 && GetFirstElement(rooms)?.isActive === false) && <NoDataFound />}
            {rooms && rooms?.length === 0 && <NoDataFound />}
          </Row> : <Spin tip="Uploading images..." />
        }
        {isAccessible &&
          <div>
            <Button
              onClick={(): void => onAddRooms()}
              disabled={isUploading}
              className="mt-md"
            >
              + Add Room
          </Button>
          </div>
        }
      </div>
      {visible && (
        <EditRoom
          disabled={!isAccessible}
          visible={visible}
          room={ref.current.editRoom}
          existingRooms={rooms}
          setSelectedPhotos={setSelectedPhotos}
          onDeleteRoom={onDeleteRoom}
          roomType={roomTypes}
          roomTypeMaster={roomTypes}
          title={'Room Details'} onRoomSave={onRoomSave} onCancel={(): void => setVisible(false)} onRoomtypeChange={onRoomtypeChange} />

      )}
    </>
  );
};

export default Rooms;
